import { useState, useCallback, useMemo } from "react";
import NewsletterInline from "../components/NewsletterInline";
import LoginModal from "../components/LoginModal";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import "./EmailAuthSection.css";

/* =========================================
   Nickelodeon blob generator (nested rings)
   Returns 7 concentric blob paths that look
   like scaled copies of the same slime shape
   ========================================= */

type BlobParams = {
  rings?: number;
  points?: number;
  baseRadius?: number;
  ringGap?: number;
  wobble?: number;
  seed?: number;
  x?: number;
  y?: number;
};

function mulberry32(seed: number) {
  let t = seed >>> 0;
  return () => {
    t += 0x5d6b79f96;
    let x = Math.imul(t ^ (t >>> 15), 1 | t);
    x ^= x + Math.imul(x ^ (x >>> 7), 61 | x);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

function polarToXY(cx: number, cy: number, r: number, a: number) {
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function toSmoothClosedPath(points: { x: number; y: number }[]) {
  if (points.length < 2) return "";
  const d: string[] = [];
  const n = points.length;

  const p = (i: number) => points[(i + n) % n];

  d.push(`M ${p(0).x.toFixed(2)} ${p(0).y.toFixed(2)}`);

  // Catmull Rom to Bezier for smooth slime
  for (let i = 0; i < n; i++) {
    const p0 = p(i - 1);
    const p1 = p(i);
    const p2 = p(i + 1);
    const p3 = p(i + 2);

    const tension = 0.5; // higher = tighter, lower = gooier
    const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension;
    const cp1y = p1.y + ((p2.y - p0.y) / 6) * tension;
    const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension;
    const cp2y = p2.y - ((p3.y - p1.y) / 6) * tension;

    d.push(
      `C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)} ${cp2x.toFixed(2)} ${cp2y.toFixed(
        2
      )} ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`
    );
  }

  d.push("Z");
  return d.join(" ");
}

function generateNickBlobPaths({
  rings = 7,
  points = 120,
  baseRadius = 46,
  ringGap = 36,
  wobble = 18,
  seed = 27,
  x = 400,
  y = 300,
}: BlobParams) {
  const rand = mulberry32(seed);

  // Build one "master" blob profile that all rings share
  // This keeps rings looking like scaled copies, not random mismatches
  const phaseA = rand() * Math.PI * 2;
  const phaseB = rand() * Math.PI * 2;
  const phaseC = rand() * Math.PI * 2;

  const freqA = 2 + Math.floor(rand() * 2); // 2..3
  const freqB = 4 + Math.floor(rand() * 2); // 4..5
  const freqC = 7 + Math.floor(rand() * 2); // 7..8

  const profile: number[] = [];

  for (let i = 0; i < points; i++) {
    const t = i / points;
    const a = t * Math.PI * 2;

    // multi harmonic goo with slight asymmetry
    const n =
      Math.sin(a * freqA + phaseA) * 0.55 +
      Math.sin(a * freqB + phaseB) * 0.30 +
      Math.sin(a * freqC + phaseC) * 0.15;

    // add a gentle "nick splat" bias so it feels cartoony
    // pushes one side a bit more, like a blob that landed
    const splatBias = Math.cos(a - 0.9) * 0.22;

    profile.push(n + splatBias);
  }

  const paths: string[] = [];

  for (let ring = 0; ring < rings; ring++) {
    const rBase = baseRadius + ring * ringGap;

    // Outer rings are calmer so the stack reads clean
    const ringWobble = wobble * (1 - ring / (rings + 0.8)) + 3;

    const pts: { x: number; y: number }[] = [];

    for (let i = 0; i < points; i++) {
      const a = (i / points) * Math.PI * 2;
      const r = rBase + profile[i] * ringWobble;
      pts.push(polarToXY(x, y, r, a));
    }

    paths.push(toSmoothClosedPath(pts));
  }

  return paths;
}

const EmailAuthSection = () => {
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLoginClick = useCallback(() => {
    setIsLoginOpen(true);
  }, []);

  const handleLoginClose = useCallback(() => {
    setIsLoginOpen(false);
  }, []);

  const handleLoginSuccess = useCallback(
    (userData: { email: string; name: string; username: string; id: string }) => {
      login(userData);
      setIsLoginOpen(false);
      navigate("/community");
    },
    [login, navigate]
  );

  // change seed to get a new blob
  // change wobble to get more or less slime
  const topoPaths = useMemo(
    () =>
      generateNickBlobPaths({
        rings: 7,
        points: 130,
        baseRadius: 34,
        ringGap: 42,
        wobble: 20,
        seed: 27,
        x: 400,
        y: 300,
      }),
    []
  );

  return (
    <section id="email-section" className="email-auth-section">
      <div className="blob-vortex" aria-hidden="true">
        <svg viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
          {topoPaths.map((d, idx) => (
            <path key={idx} d={d} />
          ))}
        </svg>
      </div>

      <div className="email-auth-content">
        <h2 className="email-auth-title">tune into the signal</h2>
        <p className="email-auth-description">
          a free community space for updates, experiments, and open sessions
        </p>

        <NewsletterInline />

        <p className="email-auth-fineprint">
          open to everyone. find exclusives and early access on my patreon.
        </p>

        <div className="email-auth-buttons">
          <button type="button" className="email-auth-link" onClick={handleLoginClick}>
            sign up/sign in
          </button>
        </div>
      </div>

      <LoginModal
        isOpen={isLoginOpen}
        onClose={handleLoginClose}
        onLoginSuccess={handleLoginSuccess}
      />
    </section>
  );
};

export default EmailAuthSection;
